"use client"

import { useMemo, useState } from "react"
import { useParams } from "next/navigation"

import { addToCart } from "@lib/data/cart"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import TileSwatch from "@modules/common/components/tile-swatch"

// Each item is one product's Sample variant + the display info needed for the
// swatch/price. Resolution of "which variant is the Sample" happens server-side
// in samples/page.tsx so this component just renders + toggles selection.
export type SampleItem = {
  id: string // product id (key)
  variantId: string // the Sample variant id to add to cart
  handle: string
  title: string
  look?: string | null
  color?: string | null
  color2?: string | null
  samplePrice?: number | null // numeric price for display, USD
}

type BundleBuilderProps = {
  items: SampleItem[]
}

const formatPrice = (amount?: number | null) => {
  if (amount == null || !Number.isFinite(amount)) return null
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export default function BundleBuilder({ items }: BundleBuilderProps) {
  const countryCode = useParams().countryCode as string

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [added, setAdded] = useState(0)

  const selectedIds = useMemo(
    () => items.filter((it) => selected[it.id]).map((it) => it),
    [items, selected]
  )
  const count = selectedIds.length

  const toggle = (id: string) => {
    setAdded(0)
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleAddToCart = async () => {
    if (count === 0 || isAdding) return

    setIsAdding(true)
    setAdded(0)

    // NOTE: the "5 samples for $5 / free shipping" promotion is a backend Medusa
    // promotion handled separately — here we only add the real Sample variants
    // (qty 1 each) to the cart. The discount applies automatically at the cart.
    try {
      for (const it of selectedIds) {
        await addToCart({
          variantId: it.variantId,
          quantity: 1,
          countryCode,
        })
      }
      setAdded(count)
      setSelected({})
    } finally {
      setIsAdding(false)
    }
  }

  if (!items.length) {
    return (
      <div className="bg-card border border-line rounded-2xl p-8 text-center mt-10">
        <p className="text-muted">
          Samples are loading their way to the shelf.{" "}
          <LocalizedClientLink
            href="/store"
            className="text-clay hover:text-clay-deep font-semibold"
          >
            Browse the catalog
          </LocalizedClientLink>{" "}
          in the meantime.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-12">
      {/* Builder headline */}
      <div className="max-w-2xl">
        <h2 className="font-serif text-2xl small:text-3xl text-ink font-medium leading-tight">
          5 samples for $5 · free shipping
        </h2>
        <p className="text-muted text-sm leading-relaxed mt-2">
          Samples are cut from the actual tile · ship 1–2 days from Austin, TX ·
          final sale.
        </p>
      </div>

      {/* Selectable grid */}
      <div className="grid grid-cols-2 small:grid-cols-3 medium:grid-cols-4 gap-4 mt-8 pb-28">
        {items.map((it) => {
          const isOn = !!selected[it.id]
          const price = formatPrice(it.samplePrice)
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => toggle(it.id)}
              aria-pressed={isOn}
              className={`group text-left bg-surface border rounded-2xl p-3 transition-all ${
                isOn
                  ? "border-clay ring-2 ring-clay"
                  : "border-line hover:border-clay/60"
              }`}
            >
              <div className="relative">
                <div className="aspect-square rounded-xl overflow-hidden ring-1 ring-line">
                  <TileSwatch
                    look={it.look}
                    color={it.color}
                    color2={it.color2}
                    className="h-full w-full"
                  />
                </div>
                <span
                  className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-white transition-all ${
                    isOn ? "bg-clay scale-100" : "bg-ink/20 scale-90"
                  }`}
                  aria-hidden="true"
                >
                  {isOn ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  )}
                </span>
              </div>
              <h3 className="font-medium text-ink text-sm leading-snug mt-3">
                {it.title}
              </h3>
              {price && (
                <div className="text-muted text-xs mt-1">{price} sample</div>
              )}
            </button>
          )
        })}
      </div>

      {/* Sticky summary */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 backdrop-blur-sm">
        <div className="content-container py-4 flex flex-col small:flex-row small:items-center gap-3 small:gap-6 justify-between">
          <div className="min-w-0">
            <div className="font-serif text-lg text-ink font-medium leading-tight">
              {count} {count === 1 ? "sample" : "samples"} selected
            </div>
            <div className="text-muted text-sm">
              {added > 0
                ? `${added} ${
                    added === 1 ? "sample" : "samples"
                  } added to your cart.`
                : count >= 5
                ? "Bundle ready — 5 for $5 with free shipping."
                : `Add ${5 - count} more for the 5-for-$5 bundle.`}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {added > 0 && (
              <LocalizedClientLink
                href="/cart"
                className="text-clay hover:text-clay-deep font-semibold text-sm"
              >
                View cart
              </LocalizedClientLink>
            )}
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={count === 0 || isAdding}
              className="inline-flex items-center justify-center bg-clay hover:bg-clay-deep disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-[10px] transition-colors"
            >
              {isAdding ? "Adding…" : "Add samples to cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
